import { useTranslation } from '@pancakeswap/localization'
import { Box, Skeleton, Text } from '@pancakeswap/uikit'
import { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import useSWR from 'swr'
import type { DataMeta, DexVersion, PoolStat, ProtocolStat } from 'server/info/types'
import type { HistoryOut } from 'server/info/history'
import Percent from 'views/Info/components/Percent'
import { usd } from './format'
import { Sparkline } from './Sparkline'

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
const Tile = styled(Box)<{ $accent: string }>`
  position: relative;
  padding: 14px 16px 10px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ $accent }) => $accent}, transparent 85%);
  }
`
const Value = styled(Text)`
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  overflow-wrap: anywhere;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 28px;
  }
`
const Spark = styled.div`
  margin-top: 6px;
  min-height: 40px;
  display: flex;
  align-items: flex-end;
`

/** 24 h LP fees = Σ over pools of (24 h volume × the pool's LP fee); only pools whose volume passed the chain check count, and the figure says how many. */
export function feesFromPools(pools: PoolStat[] | null | undefined): { fees24h: number | null; fees7d: number | null; counted: number; total: number } {
  if (!pools) return { fees24h: null, fees7d: null, counted: 0, total: 0 }
  let fees24h = 0
  let fees7d = 0
  let counted = 0
  for (const p of pools) {
    if (p.volume24hUSD === null || p.volume7dUSD === null) continue
    counted += 1
    fees24h += p.volume24hUSD * p.lpFee
    fees7d += p.volume7dUSD * p.lpFee
  }
  return { fees24h: counted ? fees24h : null, fees7d: counted ? fees7d : null, counted, total: pools.length }
}

const fetcher = (u: string) => fetch(u).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))

interface TileDef {
  key: string
  label: string
  value: string
  change?: number | null
  hint?: string
  spark?: number[]
  bars?: boolean
  color: string
}

export const ProtocolCards: React.FC<{ data?: ProtocolStat | null; pools?: PoolStat[] | null; meta?: DataMeta; loading?: boolean; version?: DexVersion | 'all'; withSparklines?: boolean }> = ({
  data,
  pools,
  meta,
  loading,
  version = 'all',
  withSparklines = true,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { data: week } = useSWR<HistoryOut>(withSparklines ? `/api/info/history?version=${version}&scope=protocol&range=7d` : null, fetcher, {
    refreshInterval: 300_000,
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })
  const fees = useMemo(() => feesFromPools(pools), [pools])
  const unavailable = (!data && !loading) || meta?.state === 'unavailable'
  const tvlSpark = week?.tvl.state === 'ok' ? week.tvl.points.map((p) => p.value) : undefined
  const volSpark = week?.volume.state === 'ok' ? week.volume.points.map((p) => p.value) : undefined
  const tiles: TileDef[] = [
    { key: 'tvl', label: t('Liquidity (TVL)'), value: data ? usd(data.tvlUSD) : '—', change: data?.tvlChange24h ?? null, spark: tvlSpark, color: theme.colors.primary, hint: tvlSpark ? t('7 days') : undefined },
    { key: 'volume', label: t('Volume 24H'), value: data ? usd(data.volume24hUSD) : '—', change: data?.volume24hChange ?? null, spark: volSpark, bars: true, color: theme.colors.secondary, hint: data ? `${t('7D')} ${usd(data.volume7dUSD)}` : undefined },
    {
      key: 'fees',
      label: t('LP fees 24H'),
      value: data ? usd(fees.fees24h) : '—',
      color: theme.colors.warning,
      hint: data ? (fees.fees7d !== null ? `${t('7D')} ${usd(fees.fees7d)}${fees.counted < fees.total ? ` · ${fees.counted}/${fees.total} ${t('pools')}` : ''}` : t('no priced volume')) : undefined,
    },
    { key: 'swaps', label: t('Swaps 24H'), value: data ? data.txCount24h.toLocaleString() : '—', change: data?.txCount24hChange ?? null, color: theme.colors.textSubtle, hint: data ? `${data.poolCount} ${t('pools')} · ${data.tokenCount} ${t('tokens')}` : undefined },
  ]
  return (
    <Grid data-testid="protocol-cards">
      {tiles.map((tile) => (
        <Tile key={tile.key} $accent={tile.color} data-testid={`stat-${tile.key}`}>
          <Text fontSize="12px" color="textSubtle" bold>
            {tile.label}
          </Text>
          {loading && !data ? (
            <Skeleton width="110px" height="30px" />
          ) : (
            <Value as="div" color={unavailable ? 'failure' : 'text'} title={unavailable ? 'Data unavailable' : undefined}>
              {tile.value}{' '}
              {typeof tile.change === 'number' && tile.change !== 0 && <Percent value={tile.change} fontSize="13px" display="inline" />}
            </Value>
          )}
          {tile.hint && (
            <Text fontSize="12px" color="textSubtle">
              {tile.hint}
            </Text>
          )}
          {tile.spark && tile.spark.length > 1 && (
            <Spark>
              <Sparkline values={tile.spark} color={tile.color} bars={tile.bars} label={`${tile.label}, 7 days`} />
            </Spark>
          )}
        </Tile>
      ))}
    </Grid>
  )
}

export default ProtocolCards
